<template>
  <!-- Assessments tab: the system's classification and recovery capabilities against what the assessments require (US 9370) -->
  <div class="sx-security" qid="system-assessments-tab">
    <div class="sx-kpis">
      <div class="sx-kpi" :class="{ 'is-empty': !classified }">
        <small>{{ $t('systems.assessments.system_classification_title') }}</small>
        <template v-if="classified">
          <div class="sp-cia">
            <span v-for="key in ['confidentiality', 'integrity', 'availability']" :key="key" :title="$t('systems_overview.criticality.' + key) + (system[key + '_impact'] ? ': ' + $t('impact.' + impactKey(system[key + '_impact'])) : '')">
              <b :class="impactClass(system[key + '_impact'])">{{ impactLevel(system[key + '_impact']) || '–' }}</b>{{ $t('systems_overview.criticality.' + key).slice(0, 1) }}
            </span>
          </div>
          <span :class="{ 'sx-kpi__warn': flags.business_impact_assessment }">{{ classificationText }}</span>
          <button v-if="canEdit" type="button" class="v2-link sx-kpi__edit" qid="system-edit-classification" @click="$refs.classification.show('classification')">
            <font-awesome-icon icon="pen"/>{{ $t('system_hero.edit') }}
          </button>
        </template>
        <template v-else>
          <span class="sx-kpi__none">{{ $t('system_assessments.not_classified') }}</span>
          <button v-if="canEdit" type="button" class="v2-link sx-kpi__cta" qid="system-edit-classification" @click="$refs.classification.show('classification')">
            <font-awesome-icon icon="plus"/>{{ $t('system_assessments.classify') }}
          </button>
        </template>
      </div>
      <div v-for="key in ['rto', 'rpo']" :key="key" class="sx-kpi" :class="{ 'is-empty': !hasCapability(key) }">
        <small>{{ $t('system_security.' + key) }}</small>
        <template v-if="hasCapability(key)">
          <b>{{ system[key + '_number'] + ' ' + system[key + '_type'] }}</b>
          <span :class="{ 'sx-kpi__warn': flags[key + '_business_requirement'] }">{{ requirementText(key) }}</span>
          <button v-if="canEdit" type="button" class="v2-link sx-kpi__edit" :qid="'system-edit-' + key" @click="$refs.classification.show(key)">
            <font-awesome-icon icon="pen"/>{{ $t('system_hero.edit') }}
          </button>
        </template>
        <template v-else>
          <span class="sx-kpi__none">{{ $t('system_security.not_set') }}</span>
          <button v-if="canEdit" type="button" class="v2-link sx-kpi__cta" :qid="'system-edit-' + key" @click="$refs.classification.show(key)">
            <font-awesome-icon icon="plus"/>{{ $t('system_assessments.set_' + key) }}
          </button>
        </template>
      </div>
      <div class="sx-kpi" :class="{ 'is-empty': loaded && !latest }">
        <small>{{ $t('system_assessments.latest') }}</small>
        <template v-if="latest">
          <b>{{ formatDate(latest.completed_at || latest.updated_at) }}</b>
          <span>{{ latest.title }}</span>
        </template>
        <span v-else class="sx-kpi__none">{{ $t('system_assessments.none_yet') }}</span>
      </div>
    </div>

    <section class="v2-card sp-sec">
      <div class="sp-sec__head">
        <font-awesome-icon icon="chart-line"/>
        <h3>{{ $t('system_assessments.title') }}</h3>
        <span class="sp-sec__state">{{ $t('system_assessments.subtitle') }}</span>
      </div>
      <div v-if="items.length" class="sx-ba sx-ba--head">
        <div>{{ $t('system_assessments.assessment') }}</div>
        <div>{{ $t('projects.table.type') }}</div>
        <div>{{ $t('systems.assessments.table_highest_impact') }}</div>
        <div>{{ $t('system_security.rto') }}</div>
        <div>{{ $t('system_security.rpo') }}</div>
        <div></div>
      </div>
      <div v-for="item in items" :key="item.id" class="sx-ba" :qid="'system-assessment-' + item.id">
        <div>
          <router-link :to="{ name: 'projects.view', params: { slug: item.slug } }" class="sx-li__t">{{ item.title }}</router-link>
          <div class="sx-li__d">{{ $t('system_assessments.finished', { date: formatDate(item.completed_at || item.updated_at) }) }}</div>
        </div>
        <div><span v-if="item.assessment" class="sp-chip">{{ item.assessment.name }}</span></div>
        <div class="sp-cia">
          <span v-for="key in ['confidentiality', 'integrity', 'availability']" :key="key" :title="$t('systems_overview.criticality.' + key) + (item[key] ? ': ' + $t('impact.' + impactKey(item[key])) : '')">
            <b :class="impactClass(item[key])">{{ impactLevel(item[key]) || '–' }}</b>{{ $t('systems_overview.criticality.' + key).slice(0, 1) }}
          </span>
        </div>
        <div v-for="key in ['rto', 'rpo']" :key="key">
          <template v-if="item[key] && Number(item[key][key + '_number']) > 0">
            <b>{{ item[key][key + '_number'] }} {{ item[key][key + '_type'] }}</b>
            <div v-if="item[key + '_business_requirement']" class="sx-li__d sx-kpi__warn" :title="$t('systems.assessments.table_warning_tooltip')">
              <font-awesome-icon icon="exclamation-triangle"/> {{ $t('systems.assessments.business_requirement_not_met') }}
            </div>
          </template>
          <span v-else class="sx-li__d">–</span>
        </div>
        <div class="text-right">
          <AssessmentViewPanel v-if="isGranted([permissions.SYSTEM_VIEW, permissions.SYSTEM_VIEW_OWN], system)" :project="item" :object="system"></AssessmentViewPanel>
        </div>
      </div>
      <p v-if="loaded && !items.length" class="sx-empty">{{ $t('system_assessments.empty') }}</p>
      <div v-if="items.length < total" class="sp-sec__foot">
        <button type="button" class="v2-link" @click="load(page + 1)">{{ $t('system_assessments.more', { count: total - items.length }) }}</button>
      </div>
    </section>

    <SystemClassificationPanel ref="classification" :system="system" @saved="$emit('updated')"></SystemClassificationPanel>
  </div>
</template>

<script>
  import moment from 'moment';
  import piincHttp from '@/util/configureAxios.js';
  import {impactClass, impactLevel, impactKey} from '@/util/impactLevels.js';
  import AssessmentViewPanel from '@/components/assessment/AssessmentViewPanel.vue';
  import SystemClassificationPanel from '@/views/Systems/page/SystemClassificationPanel.vue';

  export default {
    name: 'SystemAssessmentsTab',
    components: {
      AssessmentViewPanel,
      SystemClassificationPanel
    },
    props: {
      system: { type: Object, required: true }
    },
    data: function() {
      return {
        items: [],
        total: 0,
        page: 1,
        loaded: false,
        flags: {}
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      canEdit() {
        return this.isGranted([this.permissions.SYSTEM_EDIT, this.permissions.SYSTEM_EDIT_OWN], this.system) && (!this.getClient.parent || this.system.partition);
      },
      // The list comes newest first
      latest() {
        return this.items[0] || null;
      },
      classified() {
        return ['confidentiality', 'integrity', 'availability'].some(key => this.system[key + '_impact']);
      },
      classificationText() {
        let system = this.system;
        if (!system.confidentiality_impact && !system.integrity_impact && !system.availability_impact) {
          return this.$t('system_assessments.not_classified');
        }
        if (!this.items.length) {
          return this.$t('system_assessments.no_requirement');
        }
        return this.flags.business_impact_assessment ? this.$t('systems.assessments.higher_than_classification') : this.$t('systems.assessments.lower_than_classification');
      }
    },
    watch: {
      system: function(value, old) {
        if (!old || value.updated_at !== old.updated_at) {
          this.load(1);
        }
      }
    },
    methods: {
      impactClass: impactClass,
      impactLevel: impactLevel,
      impactKey: impactKey,
      formatDate(value) {
        return value ? moment(value).format('D MMM YYYY') : '';
      },
      hasCapability(key) {
        return !!(this.system[key + '_number'] && this.system[key + '_type']);
      },
      requirementText(key) {
        if (!this.system[key + '_number'] || !this.system[key + '_type']) {
          return this.$t('system_assessments.capability_missing');
        }
        if (!this.items.length) {
          return this.$t('system_assessments.no_requirement');
        }
        return this.flags[key + '_business_requirement'] ? this.$t('systems.assessments.business_requirement_not_met') : this.$t('systems.assessments.business_requirement_met');
      },
      load(page) {
        let self = this;
        piincHttp.get('systems/' + this.system.slug + '/assessments', { params: { page: page, per_page: 10, client: this.getClient.slug } }).then(function(response) {
          let items = response.data.projects.items;
          self.items = page > 1 ? self.items.concat(items) : items;
          self.total = response.data.projects.total_count;
          self.page = page;
          self.flags = {
            business_impact_assessment: response.data.business_impact_assessment,
            rto_business_requirement: response.data.rto_business_requirement,
            rpo_business_requirement: response.data.rpo_business_requirement
          };
          self.loaded = true;
        }, function() {});
      }
    },
    created() {
      this.load(1);
    }
  }
</script>
